import { useState } from "react";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { ImagePrompt } from "@shared/schema";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Copy, Trash2, Edit } from "lucide-react";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { usePageTitle } from "@/hooks/use-page-title";

const ASPECT_RATIOS = [
  { value: "1280x720", label: "1280×720 (HD)" },
  { value: "1600x900", label: "1600×900 (HD+)" },
  { value: "1920x1080", label: "1920×1080 (Full HD)" },
  { value: "2560x1440", label: "2560×1440 (QHD)" },
  { value: "3840x2160", label: "3840×2160 (4K)" },
];

function PromptsTab() {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("image_generator");
  const { toast } = useToast();
  const [editingPrompt, setEditingPrompt] = useState<ImagePrompt | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [formName, setFormName] = useState("");
  const [formText, setFormText] = useState("");

  const promptsQuery = useQuery<ImagePrompt[]>({
    queryKey: ["/api/app/image-prompts"],
  });

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; promptText: string }) => {
      await apiRequest("POST", "/api/app/image-prompts", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/image-prompts"] });
      setCreateOpen(false);
      setFormName("");
      setFormText("");
      toast({ title: "Prompt created" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Record<string, string> }) => {
      await apiRequest("PATCH", `/api/app/image-prompts/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/image-prompts"] });
      setEditingPrompt(null);
      setFormName("");
      setFormText("");
      toast({ title: "Prompt updated" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/image-prompts/${id}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/image-prompts"] });
      toast({ title: "Prompt deleted" });
    },
  });

  const handleDuplicate = (prompt: ImagePrompt) => {
    createMutation.mutate({
      name: `${prompt.name} (copy)`,
      promptText: prompt.promptText,
    });
  };

  const handleEditStart = (prompt: ImagePrompt) => {
    setEditingPrompt(prompt);
    setFormName(prompt.name);
    setFormText(prompt.promptText);
  };

  const prompts = promptsQuery.data || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900" data-testid="text-prompts-heading" data-tour="image-generator-rooms">Prompts ({prompts.length})</h3>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button disabled={readOnly} size="sm" className="bg-brand" data-testid="button-create-prompt" data-tour="image-generator-generate">
              <Plus className="h-4 w-4 mr-1" /> New Prompt
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Prompt</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (formName.trim() && formText.trim()) {
                  createMutation.mutate({ name: formName.trim(), promptText: formText.trim() });
                }
              }}
              className="space-y-4 mt-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <Input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Modern Living Room"
                  data-testid="input-prompt-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prompt Text</label>
                <Textarea
                  value={formText}
                  onChange={(e) => setFormText(e.target.value)}
                  placeholder="Describe the image generation prompt..."
                  rows={4}
                  data-testid="textarea-prompt-text"
                />
              </div>
              <Button type="submit" className="w-full bg-brand" disabled={readOnly || createMutation.isPending} data-testid="button-submit-prompt">
                {createMutation.isPending ? "Creating..." : "Create Prompt"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={!!editingPrompt} onOpenChange={(open) => { if (!open) setEditingPrompt(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Prompt</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (editingPrompt && formName.trim() && formText.trim()) {
                updateMutation.mutate({ id: editingPrompt.id, data: { name: formName.trim(), promptText: formText.trim() } });
              }
            }}
            className="space-y-4 mt-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                data-testid="input-edit-prompt-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prompt Text</label>
              <Textarea
                value={formText}
                onChange={(e) => setFormText(e.target.value)}
                rows={4}
                data-testid="textarea-edit-prompt-text"
              />
            </div>
            <Button type="submit" className="w-full bg-brand" disabled={readOnly || updateMutation.isPending} data-testid="button-save-prompt">
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {promptsQuery.isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : prompts.length === 0 ? (
        <div className="text-center py-12 border rounded-lg">
          <p className="text-gray-500" data-testid="text-no-prompts">No prompts created yet. Create your first prompt to get started.</p>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Prompt Preview</TableHead>
                <TableHead className="w-[140px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {prompts.map(prompt => (
                <TableRow key={prompt.id} data-testid={`row-prompt-${prompt.id}`}>
                  <TableCell className="font-medium" data-testid={`text-prompt-name-${prompt.id}`}>{prompt.name}</TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-500 line-clamp-2" data-testid={`text-prompt-preview-${prompt.id}`}>
                      {prompt.promptText}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button disabled={readOnly} size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleEditStart(prompt)} data-testid={`button-edit-prompt-${prompt.id}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button disabled={readOnly} size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDuplicate(prompt)} data-testid={`button-duplicate-prompt-${prompt.id}`}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button disabled={readOnly} size="icon" variant="ghost" className="h-7 w-7" onClick={() => deleteMutation.mutate(prompt.id)} data-testid={`button-delete-prompt-${prompt.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-gray-400 hover:text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("image_generator");
  const { toast } = useToast();

  const settingsQuery = useQuery<{ aspectRatio: string }>({
    queryKey: ["/api/app/image-generator/settings"],
  });

  const updateMutation = useMutation({
    mutationFn: async (aspectRatio: string) => {
      await apiRequest("PUT", "/api/app/image-generator/settings", { aspectRatio });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/image-generator/settings"] });
      toast({ title: "Settings saved" });
    },
  });

  const currentRatio = settingsQuery.data?.aspectRatio || "1920x1080";

  return (
    <div>
      <div className="border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-testid="text-settings-heading">Image Generation Settings</h3>
        <div className="max-w-md">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Aspect Ratio</label>
          <p className="text-sm text-gray-500 mb-3">This setting applies globally to all generated images.</p>
          <Select disabled={readOnly} value={currentRatio} onValueChange={(val) => updateMutation.mutate(val)}>
            <SelectTrigger data-testid="select-aspect-ratio">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ASPECT_RATIOS.map(ar => (
                <SelectItem key={ar.value} value={ar.value}>{ar.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-400 mt-2" data-testid="text-current-ratio">
            Current: {ASPECT_RATIOS.find(ar => ar.value === currentRatio)?.label || currentRatio}
          </p>
        </div>
      </div>
    </div>
  );
}

function PromptSettingsTabs({
  activeTab,
  setActiveTab,
}: {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}) {
  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="mb-6 border-b w-full justify-start rounded-none bg-transparent p-0" data-testid="tabs-prompt-library">
        <TabsTrigger value="prompts" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:shadow-none" data-testid="tab-prompts">
          Prompts
        </TabsTrigger>
        <TabsTrigger value="settings" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand data-[state=active]:shadow-none" data-testid="tab-settings">
          Settings
        </TabsTrigger>
      </TabsList>

      <TabsContent value="prompts">
        <PromptsTab />
      </TabsContent>

      <TabsContent value="settings">
        <SettingsTab />
      </TabsContent>
    </Tabs>
  );
}

export function InlinePromptSettingsTabs() {
  const [activeTab, setActiveTab] = useState("prompts");
  return (
    <div data-testid="inline-prompt-settings">
      <ViewOnlyBanner area="image_generator" />
      <PromptSettingsTabs activeTab={activeTab} setActiveTab={setActiveTab} />
    </div>
  );
}

export default function AppPromptLibrary() {
  usePageTitle("Prompt Library");
  const [activeTab, setActiveTab] = useTabQueryParam("tab", "prompts", {
    tabs: ["prompts", "settings"],
  });
  return (
    <AppLayout>
      <div className="p-6">
        <ViewOnlyBanner area="image_generator" />
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="text-prompt-library-title" data-tour="image-generator-heading">Prompts & Settings</h1>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="image-generator" />
            <GuideButton pageKey="image-generator" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="image-generator" />

        <PromptSettingsTabs activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>
    </AppLayout>
  );
}
